import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import SignaturePad from 'signature_pad';
import { useTheme } from '@/contexts/ThemeProvider';

interface TermsSignaturePadProps {
  onSignatureChange: (signatureData: string | null) => void;
  disabled?: boolean;
  existingSignature?: string | null;
  fullScreen?: boolean;
  mobileInModal?: boolean;
  hideLabel?: boolean;
  setCurrentSignature?: (signature: string | null) => void;
}

const TermsSignaturePad: React.FC<TermsSignaturePadProps> = ({
  onSignatureChange,
  disabled = false,
  existingSignature = null,
  fullScreen = false,
  mobileInModal = false,
  hideLabel = false,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const [isEmpty, setIsEmpty] = useState(!existingSignature);
  const [showingExisting, setShowingExisting] = useState(!!existingSignature);

  const { theme, systemTheme } = useTheme();

  const actualTheme = theme === 'system' ? systemTheme : theme;

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const signaturePad = new SignaturePad(canvas, {
      backgroundColor: 'transparent',
      penColor: actualTheme === 'dark' ? '#ffffff' : '#000000',
      minWidth: 0.5,
      maxWidth: 2.5,
      throttle: 16,
      minDistance: 1,
      velocityFilterWeight: 0.7,
    });

    signaturePadRef.current = signaturePad;

    // Handle signature events
    const handleSignature = () => {
      // Use a small delay to ensure the stroke is fully rendered
      const isEmpty = signaturePad.isEmpty();
      console.log('🖊️ Signature event:', { isEmpty, hasData: !isEmpty });
      setIsEmpty(isEmpty);

      if (!isEmpty) {
        setShowingExisting(true); // User is creating new signature
        setIsEmpty(false);
        const dataURL = signaturePad.toDataURL('image/png');
        console.log('📝 Signature captured:', {
          length: dataURL.length,
          preview: dataURL.substring(0, 50) + '...'
        });
        onSignatureChange(dataURL);
      }

    };



    signaturePad.addEventListener('beginStroke', handleSignature);
    signaturePad.addEventListener('endStroke', handleSignature);

    // Resize canvas to fit container
    const resizeCanvas = () => {
      const container = canvas.parentElement;
      if (!container) return;

      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const rect = container.getBoundingClientRect();

      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(ratio, ratio);
      }

      // Restore signature pad configuration after resize
      signaturePad.clear();

      // Load existing signature after canvas resize
      if (existingSignature && showingExisting) {
        try {
          console.log('🔄 Loading existing signature:');
          signaturePad.fromDataURL(existingSignature);
          setIsEmpty(false);
        } catch (error) {
          console.error('Error loading existing signature:', error);
          setIsEmpty(true);
          setShowingExisting(false);
        }
      } else {
        setIsEmpty(true);
      }
    };

    resizeCanvas();

    window.addEventListener('resize', resizeCanvas);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      signaturePad.removeEventListener('beginStroke', handleSignature);
      signaturePad.removeEventListener('endStroke', handleSignature);
    };
  }, [onSignatureChange, existingSignature, showingExisting, actualTheme]);

  // Calculate height class based on props
  const getHeightClass = () => {
    if (mobileInModal) {
      return 'h-[40vh] min-h-[300px]';
    }
    if (fullScreen) {
      return 'h-64 sm:h-80';
    }
    return 'h-32 md:h-40';
  };

  const clearSignature = () => {
    if (signaturePadRef.current) {
      console.log('🧹 Clearing signature');
      signaturePadRef.current.clear();
      setIsEmpty(true);
      setShowingExisting(false);
      onSignatureChange(null);
    }
  };

  const replaceSignature = () => {
    console.log('🔄 Replacing existing signature');
    setShowingExisting(false);
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setIsEmpty(true);
      onSignatureChange(null);
    }
  };

  return (
    <div className={`space-y-4 ${mobileInModal ? 'flex-1 flex flex-col' : ''}`}>
      {!hideLabel && (
        <Label className="text-sm font-medium">
          {t('clients.termsAcceptance.signature')}
        </Label>
      )}

      <Card className={`border-2 border-dashed border-border ${mobileInModal ? 'flex-1' : ''}`}>
        <CardContent className="p-4 h-full">
          <div className="relative">
            <div
              className={`w-full ${getHeightClass()} bg-background border border-input rounded-md relative overflow-hidden`}
              style={{
                opacity: disabled ? 0.5 : 1,
                pointerEvents: disabled ? 'none' : 'auto'
              }}
            >
              <canvas
                ref={canvasRef}
                className="absolute inset-0 w-full h-full touch-manipulation"
                style={{
                  touchAction: 'none',
                  cursor: disabled ? 'not-allowed' : 'crosshair'
                }}
              />

              {isEmpty && !disabled && !showingExisting && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="text-sm text-muted-foreground">
                    {t('clients.termsAcceptance.signHere')}
                  </span>
                </div>
              )}
            </div>

            {showingExisting && existingSignature && !disabled && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={replaceSignature}
                className="absolute top-2 right-2"
              >
                {t('clients.termsAcceptance.replaceSignature')}
              </Button>
            )}

            {!isEmpty && !disabled && !showingExisting && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clearSignature}
                className="absolute top-2 right-2"
              >
                {t('clients.termsAcceptance.clearSignature')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TermsSignaturePad;